// Small reusable loading-spinner utility. A global in-flight counter drives
// a spinner in the header (visible whenever anything is loading); callers
// that want a more localized indicator (e.g. next to "Loading packages…")
// can also grab a standalone spinnerElement().
let count = 0;

function indicatorEl(): HTMLElement | null {
  return document.getElementById("global-spinner");
}

function updateIndicator(): void {
  const el = indicatorEl();
  if (el) el.style.display = count > 0 ? "inline-block" : "none";
}

export function beginLoading(): void {
  count++;
  updateIndicator();
}

export function endLoading(): void {
  count = Math.max(0, count - 1);
  updateIndicator();
}

/** Wrap an async operation so the global spinner shows for its duration,
 * regardless of whether it succeeds or throws. */
export async function withLoading<T>(fn: () => Promise<T>): Promise<T> {
  beginLoading();
  try {
    return await fn();
  } finally {
    endLoading();
  }
}

export function spinnerElement(): HTMLElement {
  const span = document.createElement("span");
  span.className = "spinner";
  return span;
}

/** Dims the map and shows a large centred spinner (app.css's
 * .map-loading-overlay) over #map-area — for any operation long/disruptive
 * enough that a user could otherwise mistake it for nothing happening
 * (originally built for the area-package wizard's live preview recompute,
 * now shared with the "Save and re-run" package-settings action). Only one
 * such overlay is ever meaningful at a time, so this is a single shared
 * singleton keyed by a fixed element id rather than one per caller. */
export function setMapLoadingOverlay(visible: boolean): void {
  const mapArea = document.getElementById("map-area");
  if (!mapArea) return;
  const existing = document.getElementById("map-loading-overlay");
  if (!visible) {
    existing?.remove();
    return;
  }
  if (existing) return; // already showing
  const overlay = document.createElement("div");
  overlay.id = "map-loading-overlay";
  overlay.className = "map-loading-overlay";
  const spinner = document.createElement("div");
  spinner.className = "spinner-large";
  overlay.append(spinner);
  mapArea.append(overlay);
}

export function isMapLoadingOverlayVisible(): boolean {
  return document.getElementById("map-loading-overlay") !== null;
}
